import { HttpClientModule } from '@angular/common/http';
import { ApplicationRef, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PreloadAllModules, RouterModule } from '@angular/router';
import { createInputTransfer, createNewHosts, removeNgStyles } from '@angularclass/hmr';
import { CookieService } from 'ngx-cookie-service';

import '../styles/main.scss';

import { AppComponent } from './app.component';
import { ROUTER_CONFIG } from './app.routes';
import { LOCAL_STORAGE_LANG_KEY } from './common/constants';
import { CoreModule } from './core/core.module';
import { ConfigService } from './core/providers/config/config.service';
import { I18nService, UILanguage } from './core/providers/i18n/i18n.service';
import { SearchEffectsService } from './core/providers/search/search-effects.service';
import { httpInterceptorProviders } from './http-interceptors';
import { AuthEffectsService } from './login/providers/auth-effects.service';
import { SharedModule } from './shared/shared.module';
import { AppState } from './state/models/app-state.model';
import { ApplicationStateService } from './state/providers/application-state.service';
import { StateModule } from './state/state.module';

// Data type for saving and restoring application state with hot module reloading
interface HmrStore {
    state: AppState;
    restoreInputValues(): void;
    disposeOldHosts(): void;
}

/**
 * `AppModule` is the main entry point into Angular's bootstraping process
 */
@NgModule({
    bootstrap: [AppComponent],
    declarations: [AppComponent],
    providers: [httpInterceptorProviders, CookieService],
    imports: [
        RouterModule.forRoot(ROUTER_CONFIG, { useHash: true, preloadingStrategy: PreloadAllModules }),
        BrowserModule,
        BrowserAnimationsModule,
        HttpClientModule,
        CoreModule,
        SharedModule,
        StateModule
    ]
})
export class AppModule {
    constructor(
        public appRef: ApplicationRef,
        public i18nService: I18nService,
        public appState: ApplicationStateService,
        config: ConfigService,
        authEffects: AuthEffectsService,
        searchEffects: SearchEffectsService
    ) {
        const defaultLanguage = (localStorage.getItem(LOCAL_STORAGE_LANG_KEY) || config.DEFAULT_LANGUAGE) as UILanguage;
        appState.actions.ui.setLanguage(defaultLanguage);
        i18nService.setLanguage(defaultLanguage);
        authEffects.validateSession();
        searchEffects.checkSearchAvailability();
        // router.events.subscribe(event => console.log(event));
    }

    public hmrOnInit(store: HmrStore) {
        if (!store || !store.state) {
            return;
        }
        // console.log('HMR store', JSON.stringify(store, null, 2));
        // set state
        this.appState.restore(store.state);
        // set input values
        if ('restoreInputValues' in store) {
            const restoreInputValues = store.restoreInputValues;
            setTimeout(restoreInputValues);
        }

        this.appRef.tick();
        delete store.state;
        delete store.restoreInputValues;
    }

    public hmrOnDestroy(store: HmrStore) {
        const cmpLocation = this.appRef.components.map(cmp => cmp.location.nativeElement);
        // save state
        store.state = this.appState.now;
        // recreate root elements
        store.disposeOldHosts = createNewHosts(cmpLocation);
        // save input values
        store.restoreInputValues = createInputTransfer();
        // remove styles
        removeNgStyles();
    }

    public hmrAfterDestroy(store: HmrStore) {
        // display new elements
        store.disposeOldHosts();
        delete store.disposeOldHosts;
    }
}
